import { useLingui } from '@lingui/react/macro'
import { Input } from 'designSystem/components/Input'
import {
  CoverageFilter,
  matchesCoverageFilter,
  TagCoverage,
} from 'features/sentinels/coverage'
import { CoverageDot } from 'features/sentinels/CoverageDot'
import {
  BrowserContainer,
  ChipsRow,
  ListRegion,
  SearchRow,
  StatusLine,
  TagRowButton,
  TagRowDescription,
  TagRowName,
  TagRowText,
  TagRowUnit,
} from 'features/timeSeries/components/panes/tagBrowserList.styles'
import {
  CheckMark,
  KeywordChip,
} from 'features/timeSeries/components/panes/TagFilterControls'
import {
  ALL_SOURCES,
  emptyTagListCopy,
  MIN_SEARCH_LENGTH,
  toggleTagSelection,
  useAvailabilityFilteredTags,
} from 'features/timeSeries/components/panes/tagFilters'
import { useElementSize } from 'features/timeSeries/components/panes/useElementSize'
import { MAX_PENS } from 'features/timeSeries/config/constants'
import { normalizeSearchText } from 'features/timeSeries/hooks/useTagFiltering'
import { useScatterplotContext } from 'features/timeSeries/ScatterplotContext'
import React, { useCallback, useMemo, useState } from 'react'
import { FixedSizeList, ListChildComponentProps } from 'react-window'
import { BaseSensorEntry } from 'types'
import { MOBILE_LAYOUT_QUERY } from 'utils/mobileLayout'

/* Wide rails read better as a table: name and description as columns
   instead of a two-line stack. */
const COLUMN_LAYOUT_MIN_WIDTH = 480

/* Shares the dialog's normalization so a query never matches differently
   between the rail and the expanded search. */
const matchesTerm = (tag: BaseSensorEntry, term: string): boolean => {
  const normalizedTerm = normalizeSearchText(term)
  const name = normalizeSearchText(tag.data.name ?? '')
  const description = normalizeSearchText(tag.data.description ?? '')
  return name.includes(normalizedTerm) || description.includes(normalizedTerm)
}

interface TagRowData {
  tags: BaseSensorEntry[]
  selectedIds: Set<string>
  atPenCap: boolean
  showDetails: boolean
  columns: boolean
  coverageById?: Map<string, TagCoverage>
  onToggle: (tag: BaseSensorEntry) => void
}

const Row: React.FC<ListChildComponentProps<TagRowData>> = ({
  index,
  style,
  data,
}) => {
  const { t } = useLingui()

  const tag = data.tags[index]
  const isSelected = data.selectedIds.has(tag.id)
  const disabled = !isSelected && data.atPenCap
  const shared = {
    type: 'button' as const,
    style,
    disabled,
    'aria-pressed': isSelected,
    title: disabled ? t`Pen limit reached (${MAX_PENS})` : tag.data.name,
    onClick: () => data.onToggle(tag),
  }
  const coverageDot = data.coverageById && (
    <CoverageDot coverage={data.coverageById.get(tag.id)} />
  )
  if (data.columns) {
    return (
      <TagRowButton $columns {...shared}>
        <CheckMark checked={isSelected} />
        {coverageDot}
        <TagRowName>{tag.data.name}</TagRowName>
        <TagRowDescription>{tag.data.description ?? ''}</TagRowDescription>
        <TagRowUnit>{tag.data.unit ?? ''}</TagRowUnit>
      </TagRowButton>
    )
  }
  return (
    <TagRowButton {...shared}>
      <CheckMark checked={isSelected} />
      {coverageDot}
      <TagRowText>
        <TagRowName>{tag.data.name}</TagRowName>
        {data.showDetails && tag.data.description && (
          <TagRowDescription>{tag.data.description}</TagRowDescription>
        )}
      </TagRowText>
      {tag.data.unit && <TagRowUnit>{tag.data.unit}</TagRowUnit>}
    </TagRowButton>
  )
}

export interface TagListCoverage {
  byId: Map<string, TagCoverage>
  filter: CoverageFilter
  onFilterChange: (filter: CoverageFilter) => void
}

interface TagBrowserListProps {
  showDetails: boolean
  coverage?: TagListCoverage
}

export const TagBrowserList: React.FC<TagBrowserListProps> = ({
  showDetails,
  coverage,
}) => {
  const { t } = useLingui()
  const [autoFocusSearch] = useState(
    () => !window.matchMedia(MOBILE_LAYOUT_QUERY).matches
  )

  const { sensorEntries, selectedSensors, setSelectedSensors } =
    useScatterplotContext()
  const [query, setQuery] = useState('')
  const [chips, setChips] = useState<string[]>([])
  const [listRef, listSize] = useElementSize()

  const terms = useMemo(() => {
    const live = query.trim().toLowerCase()
    const all = chips.map((chip) => chip.toLowerCase())
    if (live) all.push(live)
    return all
  }, [query, chips])

  const termFiltered = useMemo(() => {
    if (terms.length === 0) return sensorEntries
    return sensorEntries.filter((tag) =>
      terms.every((term) => matchesTerm(tag, term))
    )
  }, [sensorEntries, terms])

  const hasMeaningfulSearch =
    chips.length > 0 || query.trim().length >= MIN_SEARCH_LENGTH
  // The rail has no filter controls (they live in the expanded search), so
  // availability filtering runs with its defaults: every source, all tags.
  const { displayed: availabilityFiltered } = useAvailabilityFilteredTags(
    termFiltered,
    ALL_SOURCES,
    'all',
    hasMeaningfulSearch
  )

  const coverageById = coverage?.byId
  const coverageFilter = coverage?.filter ?? 'all'
  const displayed = useMemo(() => {
    if (!coverageById || coverageFilter === 'all') return availabilityFiltered
    return availabilityFiltered.filter((tag) =>
      matchesCoverageFilter(coverageFilter, coverageById.get(tag.id))
    )
  }, [availabilityFiltered, coverageById, coverageFilter])

  const selectedIds = useMemo(
    () => new Set(selectedSensors.map((tag) => tag.id)),
    [selectedSensors]
  )
  const atPenCap = selectedSensors.length >= MAX_PENS

  const handleToggle = useCallback(
    (tag: BaseSensorEntry) => {
      setSelectedSensors((prev) => toggleTagSelection(prev, tag, MAX_PENS))
    },
    [setSelectedSensors]
  )

  const handleSearchKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'Enter') return
    const term = query.trim()
    if (!term) return
    setChips((prev) => (prev.includes(term) ? prev : [...prev, term]))
    setQuery('')
  }

  const columns = showDetails && listSize.width >= COLUMN_LAYOUT_MIN_WIDTH
  const rowHeight = showDetails && !columns ? 40 : 26
  const rowData = useMemo<TagRowData>(
    () => ({
      tags: displayed,
      selectedIds,
      atPenCap,
      showDetails,
      columns,
      coverageById,
      onToggle: handleToggle,
    }),
    [
      displayed,
      selectedIds,
      atPenCap,
      showDetails,
      columns,
      coverageById,
      handleToggle,
    ]
  )

  return (
    <BrowserContainer>
      <SearchRow>
        <Input
          autoFocus={autoFocusSearch}
          inputSize='sm'
          placeholder={t`Find tags...`}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={handleSearchKeyDown}
        />
      </SearchRow>
      {chips.length > 0 && (
        <ChipsRow>
          {chips.map((chip) => (
            <KeywordChip
              key={chip}
              label={chip}
              onRemove={() =>
                setChips((prev) => prev.filter((c) => c !== chip))
              }
            />
          ))}
        </ChipsRow>
      )}

      <ListRegion ref={listRef}>
        {displayed.length === 0 ? (
          <StatusLine>
            {emptyTagListCopy('all', hasMeaningfulSearch)}
          </StatusLine>
        ) : (
          <FixedSizeList
            height={listSize.height}
            width='100%'
            itemCount={displayed.length}
            itemSize={rowHeight}
            itemData={rowData}>
            {Row}
          </FixedSizeList>
        )}
      </ListRegion>
    </BrowserContainer>
  )
}
